import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, Container, Paper, Typography } from '@mui/material';
import { useCookies } from 'react-cookie';
import Footer from './Footer';
import LawAppBar from './LawAppBar';
import './SecurityPrivacy.css';

export default function SecurityPrivacy() {
    const [cookies] = useCookies(['given_name', 'userid', 'picture', 'email', 'credential']);

    return (
        <div className="App">
            <LawAppBar cookies={cookies} />
            <Container
                maxWidth="lg"
                sx={{
                    padding: { xs: '20px', sm: '40px' },
                    marginTop: '40px',
                    marginBottom: '60px'
                }}
            >
                {/* Hero Section */}
                <Box sx={{ textAlign: 'center', marginBottom: '60px' }}>
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 400,
                            fontSize: { xs: '2rem', sm: '2.8rem', md: '3.2rem' },
                            marginBottom: '1rem',
                            letterSpacing: '-0.02em',
                            lineHeight: 1.2
                        }}
                    >
                        Security At AttorneyAide
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: { xs: '1rem', sm: '1.2rem' },
                            fontWeight: 400,
                            maxWidth: 800,
                            margin: '0 auto',
                            lineHeight: 1.6
                        }}
                    >
                        Our platform has been designed and built ground up for law professionals and employ industry-leading security measures to safeguard your sensitive data and meet the highest standards.
                    </Typography>
                </Box>

                {/* Zero-Training Privacy */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px',
                        textAlign: 'left'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                        <LockIcon
                            sx={{
                                fontSize: { xs: '2rem', sm: '2.5rem' },
                                color: 'var(--accent-color)'
                            }}
                        />
                        <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                fontSize: { xs: '1.8rem', sm: '2.2rem' },
                                letterSpacing: '-0.01em',
                                margin: 0
                            }}
                        >
                            Zero-Training Privacy
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Your data is your data. It is never used to train underlying AI models. We require model providers to maintain Zero Data Retention (ZDR) and contractually prohibit them from training on Customer Data.
                    </Typography>
                </Paper>

                {/* HIPAA-Grade Security */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px',
                        textAlign: 'left'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                        <SecurityIcon
                            sx={{
                                fontSize: { xs: '2rem', sm: '2.5rem' },
                                color: 'var(--accent-color)'
                            }}
                        />
                        <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                fontSize: { xs: '1.8rem', sm: '2.2rem' },
                                letterSpacing: '-0.01em',
                                margin: 0
                            }}
                        >
                            HIPAA Compliance
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        We treat Protected Health Information (PHI) with the highest level of care and employ industry leading measures including:
                    </Typography>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '24px', color: 'var(--text-primary)' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            End-to-end encryption for data in transit (TLS 1.3+)
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Encryption at rest using industry-standard AES-256
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            OAuth 2.0 authentication with support for multi-factor authentication (MFA). AttorneyAide strongly recommends that all users enable multi-factor authentication. AttorneyAide relies on third-party enterprise authentication providers (e.g., Google Auth Platform) for user authentication.
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Role-based access controls, access logging, and regular audits.
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px'
                        }}
                    >
                        Firms that represent Covered Entities and require a Business Associate Agreement (BAA) may execute one here: <a href="/baa" style={{ color: 'var(--text-primary)', textDecoration: 'underline' }}>AttorneyAide BAA</a>.
                    </Typography>
                </Paper>

                {/* Verifiable Accuracy */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px',
                        textAlign: 'left'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                        <VerifiedIcon
                            sx={{
                                fontSize: { xs: '2rem', sm: '2.5rem' },
                                color: 'var(--accent-color)'
                            }}
                        />
                        <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                fontSize: { xs: '1.8rem', sm: '2.2rem' },
                                letterSpacing: '-0.01em',
                                margin: 0
                            }}
                        >
                            Verifiable Accuracy with AccuAide™
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Every piece of information is linked back to the specific page in the original document, enabling one click human-in-the-loop verification. Our system is designed to flag concerns arising from illegible text, missing pages, or conflicting information for your professional review rather than guessing or hallucinating.
                    </Typography>
                </Paper>
            </Container>
            <Footer />
        </div>
    );
}
